// PAYOUT ADDRESSES (UI rebuild batch 4 · custody setup · reference build) — Astra's "find a named, approved destination":
// the list first (payee / label, network, next step, stage), Propose address as the page's one gold button on its own
// surface (ProposeAddress.tsx), and one address opened in a sheet that offers the ONE thing its stage allows.
//
// THE STAGES ARE THE SERVER'S (db/0098, the operator 24–26 Oct 2026; components/v2/custody-admin/stage.tsx):
//   Awaiting owner  only the OWNER's Approve payout address in the Books starts it (canApprovePayouts): the owner gets
//                   the link to the wallet register, anyone else the line "Only the owner approves" — no button here
//   Approved        only the 10 USDT test can be paid; it counts toward the payout (D12, 26 Oct)
//   Test sent       on its way → landed → his Received press (gold), once the customer confirms
//   Whitelisted     Pay — from Hot, or for a merchant Merchant Hot (default) / Hot / Flagged (paySources)
//   Stage not read, retired, held, archived: no button at all
// The address is shown by its HEAD only on every row and in the sheet: its ending is what he types to sign (review pack
// §7, the typing gate).
//
// Kept from screens/custody/Whitelist.tsx: the reads and keys (['custody','whitelist'] with whitelistPollMs while a test
// is out or a recorded payment's stage has not moved; ['custody','limits'] for the test's figure), the Received mutation
// and receivedRefusal, primaryReceivedId (one gold Received), listState (a failed refresh keeps the list).
// PAY OPENS THE REBUILT PAY PAGE (/custody/pay/:id, MovePages.tsx PayV2 — 3 Nov 2026, the wallet's P-063): one pay form for every
// payout, with the deal it settles; it replaces today's MoveSheet here, as this file always said it would. The page keeps its
// own press through a refresh and reads the address book again when the payment is recorded.
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus } from 'lucide-react';
import { api, custody as custodyApi, type WhitelistEntry } from '../../../lib/api';
import {
  whitelistGroup, whitelistPollMs, primaryReceivedId, landedAtOf, listState,
} from '../../../lib/custody-screen';
import { relativeTime, groupDigits, shortAddress, until } from '../../../lib/custody-format';
import { receivedRefusal } from '../../custody/refusals';
import { isDesk, payeeName } from '../../../components/v2/custody/desk';
import {
  PageHead, Panel, Notice, Register, SearchBar, SkeletonRows, EmptyState, StateLine, DetailSheet, Fact, TAP, type RegisterColumn,
} from '../../../components/v2/kit';
import { STAGE_ORDER, StagePill, nextStep, payoutHead, networkWord } from '../../../components/v2/custody-admin/stage';
import { CA, OPS, BOOKS_APPROVAL } from '../../../components/v2/custody-admin/paths';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

type Show = 'all' | 'needs' | 'payable';
const SHOW_LABEL: Record<Show, string> = { all: 'All', needs: 'Needs you', payable: 'Whitelisted' };

/** `embedded`: drawn inside custody's Wallets screen, whose head carries Propose payout address. */
export function PayoutAddressesV2({ embedded = false }: { embedded?: boolean } = {}) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [show, setShow] = useState<Show>('all');
  const [showRetired, setShowRetired] = useState(false);
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: custodyApi.whitelist, refetchInterval: (query) => whitelistPollMs(query.state.data?.entries, null) });
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: custodyApi.limits, staleTime: 30_000, retry: false });
  const testUsdt = groupDigits(limits.data?.testPaymentUsdt ?? '10');
  // The owner alone approves (N-061; WalletRegister.tsx): the server says so, and the link is offered only when the
  // identity read SAYS so — unknown is not yes (Screens and Astra, 27 Oct 2026, minor).
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const canApprove = me.data?.canApprovePayouts === true;

  const state = listState(list);
  const entries = list.data?.entries ?? [];
  const ordered = useMemo(() => STAGE_ORDER.flatMap((g) => entries.filter((e) => whitelistGroup(e) === g)), [entries]);
  const needle = q.trim().toLowerCase();
  const matches = (e: WhitelistEntry) => !needle || [payeeName(e), e.partyName, e.label, e.address].some((v) => (v ?? '').toLowerCase().includes(needle));
  const inShow = (e: WhitelistEntry) => { const g = whitelistGroup(e); return show === 'all' || (show === 'payable' ? g === 'payable' : g !== 'payable' && g !== 'unavailable'); };
  const shown = ordered.filter((e) => matches(e) && inShow(e));
  const live = shown.filter((e) => whitelistGroup(e) !== 'unavailable');
  const retired = shown.filter((e) => whitelistGroup(e) === 'unavailable');
  const rows = showRetired ? shown : live;
  const primaryId = primaryReceivedId(ordered);
  const openId = params.get('payout');
  const open = openId ? entries.find((e) => String(e.id) === openId) ?? null : null;
  const setOpen = (id: number | null) => { const n = new URLSearchParams(params); if (id === null) n.delete('payout'); else n.set('payout', String(id)); setParams(n); };

  const who = (e: WhitelistEntry) => (
    <span className="block min-w-0">
      <span className="line-clamp-2 break-words font-medium" title={payeeName(e)}>{payeeName(e)}</span>
      <span className="mt-1 block truncate text-meta text-tg-hint">{isDesk(e) ? payoutHead(e.address) : [e.label, e.partyRole === 'merchant' ? 'Merchant' : null].filter(Boolean).join(' · ') || payoutHead(e.address)}</span>
    </span>
  );
  const columns: RegisterColumn<WhitelistEntry>[] = [
    { key: 'who', head: 'Payee / label', cell: who },
    { key: 'net', head: 'Network', width: 'w-[8rem]', cell: (e) => <span className="font-mono">{networkWord(e.chain)}</span> },
    { key: 'next', head: 'Next step', align: 'right', width: 'w-[12rem]', cell: (e) => nextStep(e, testUsdt) },
    { key: 'state', head: 'Stage', align: 'right', width: 'w-[11rem]', cell: (e) => <span className="inline-flex items-center gap-2"><StagePill e={e} /><ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden /></span> },
  ];
  const retry = <button type="button" className={TAP} disabled={list.isFetching} onClick={() => void list.refetch()}>{list.isFetching ? 'Trying…' : 'Try again'}</button>;

  return (
    <>
      {!embedded && (
        <PageHead
          title="Payout addresses"
          inline
          actions={(
            <Link to={CA.propose} className="btn-primary inline-flex min-h-11 items-center gap-2 px-4 text-body lg:min-h-10">
              <Plus className="h-4 w-4" aria-hidden />Propose address
            </Link>
          )}
        />
      )}
      <SearchBar
        value={q}
        onChange={setQ}
        placeholder="Search payee or label"
        active={show === 'all' ? 0 : 1}
        filters={(
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Stage">
            {(Object.keys(SHOW_LABEL) as Show[]).map((s) => (
              <button key={s} type="button" aria-pressed={show === s} onClick={() => setShow(s)} className={`chip ${show === s ? 'chip-on' : ''}`}>{SHOW_LABEL[s]}</button>
            ))}
          </div>
        )}
      />

      {(state.show === 'failed' || state.stale) && isCustodySignin(list.error) && <SigninQuiet error={list.error} />}
      {(state.show === 'failed' || state.stale) && !isCustodySignin(list.error) && (
        <Notice tone="bad">
          <StateLine tone="bad" more={textOf(list.error)} action={retry}>
            {state.stale ? <>Couldn&rsquo;t refresh — showing the last read.</> : <>Couldn&rsquo;t read the payout addresses.</>}
          </StateLine>
        </Notice>
      )}

      {state.show === 'pending' ? (
        <Panel flush><SkeletonRows rows={4} what="payout addresses" /></Panel>
      ) : rows.length > 0 ? (
        <Panel flush>
          <Register
            label="Payout addresses"
            columns={columns}
            rows={rows}
            rowKey={(e) => String(e.id)}
            onOpen={(e) => setOpen(e.id)}
            card={(e) => ({
              title: who(e),
              figure: <ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden />,
              meta: <><span className="font-mono">{networkWord(e.chain)}</span> · {nextStep(e, testUsdt)}</>,
              state: <StagePill e={e} />,
            })}
          />
          <div className="flex justify-between gap-3 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span>{state.show === 'ready' && !state.stale ? `${live.length} in use` : ''}</span>
            <span>Dubai time</span>
          </div>
        </Panel>
      ) : state.show === 'ready' && !state.stale && retired.length === 0 ? (
        <EmptyState>{needle || show !== 'all' ? 'Nothing here.' : 'No payout addresses yet.'}</EmptyState>
      ) : null}
      {/* Retired, held and archived addresses: folded, never dropped — the count is real. */}
      {state.show === 'ready' && retired.length > 0 && (
        <button type="button" onClick={() => setShowRetired((v) => !v)} className={`${TAP} mt-3 inline-block`}>
          {showRetired ? 'Hide not payable' : `${retired.length} not payable →`}
        </button>
      )}

      {open && (
        <PayoutSheet
          e={open}
          testUsdt={testUsdt}
          canApprove={canApprove}
          primary={open.id === primaryId}
          onPay={() => navigate(`/custody/pay/${open.id}`)}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/** One address: its facts, then the one thing its stage allows — or a line and nothing. */
function PayoutSheet({ e, testUsdt, canApprove, primary, onPay, onClose }: {
  e: WhitelistEntry; testUsdt: string; canApprove: boolean; primary: boolean; onPay: () => void; onClose: () => void;
}) {
  const qc = useQueryClient();
  const group = whitelistGroup(e);
  const landedAt = landedAtOf(e);
  const test = `${testUsdt} USDT`;
  const received = useMutation({
    mutationFn: () => custodyApi.payoutTestReceived(e.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); },
  });
  const approvedByBooks = Boolean(e.approvedAt) && e.approvedVia === 'books';

  let line: ReactNode = null;
  let action: ReactNode = null;
  if (group === 'awaiting') {
    line = (
      <StateLine tone="warn" more={approvedByBooks ? 'Approved in the Books by someone who is not the owner: only the owner\'s Approve payout address starts the whitelisting.' : e.approvedAt ? 'An earlier approval from the desk IP or a Ledger message no longer counts: only the Books\' Approve payout address starts the whitelisting.' : `The owner approves it in the Books; then a ${test} test the customer confirms whitelists it.`}>
        Awaiting the owner&rsquo;s approval — nothing can be paid.
      </StateLine>
    );
    action = canApprove
      ? <Link to={BOOKS_APPROVAL} className="btn-secondary inline-flex min-h-11 w-full items-center justify-center px-4 text-body lg:min-h-10">Approve in the Books</Link>
      : <p className="text-meta text-tg-hint">Only the owner approves payout addresses.</p>;
  } else if (group === 'unknown') {
    line = (
      <StateLine tone="bad" more="The list was read without this address's whitelisting stage, so the screen cannot tell what may be paid to it. The desk's own rule still decides any payment; refresh, and tell the wallet session if it stays.">
        Stage not read — nothing can be paid here yet.
      </StateLine>
    );
  } else if (group === 'unavailable') {
    line = <StateLine tone="bad">Not payable.</StateLine>;
  } else if (group === 'approved') {
    line = (
      <StateLine more={`Whitelisting has two steps: the owner's approval in the Books, then a ${test} test the customer confirms to you. Until you press Received for it, the test is the only payment this address can receive.`}>
        Send the {test} test first — it counts toward the payout.
      </StateLine>
    );
    action = <button type="button" className="btn-primary min-h-11 w-full px-4 text-body lg:min-h-10" onClick={onPay}>Send the {test} test</button>;
  } else if (group === 'test_sent' && !landedAt) {
    line = (
      <StateLine more="Sign it in Approvals if you have not; it lands about a minute after it is sent. Nothing else can be paid here until the customer confirms it and you press Received.">
        The {test} test is on its way.
      </StateLine>
    );
    action = <Link to={OPS.approvals} className="btn-secondary inline-flex min-h-11 w-full items-center justify-center px-4 text-body lg:min-h-10">Approvals</Link>;
  } else if (group === 'test_sent') {
    line = (
      <StateLine tone="warn" more={`Landed ${relativeTime(landedAt!)}${e.testTxHash ? ` · tx ${shortAddress(e.testTxHash, 8, 6)}` : ''}. Ask the customer whether the ${test} arrived — by Telegram, phone or in person — and press Received only when they confirm. Not received? Do not press: revoke the address in the Books.`}>
        Test landed — press Received once the customer confirms.
      </StateLine>
    );
    action = (
      <>
        <button type="button" className={`${primary ? 'btn-primary' : 'btn-secondary'} min-h-11 w-full px-4 text-body lg:min-h-10`} disabled={received.isPending} onClick={() => received.mutate()}>
          {received.isPending ? 'Saving…' : 'Received'}
        </button>
        {received.isError && <StateLine tone="bad" className="mt-2" more={textOf(received.error)}>{receivedRefusal(received.error)}</StateLine>}
      </>
    );
  } else if (group === 'payable') {
    action = <button type="button" className="btn-primary min-h-11 w-full px-4 text-body lg:min-h-10" onClick={onPay}>Pay</button>;
  }

  return (
    <DetailSheet title={payeeName(e)} onClose={onClose} footer={action ?? undefined}>
      {/* The stage's one line says it; a payable address, which has no line, shows its pill. */}
      {line ? <div className="mb-4">{line}</div> : <div className="mb-4"><StagePill e={e} /></div>}
      <div className="border-t border-stroke-soft">
        {/* A desk wallet is named by the register alone (N-091): its typed label (on #43, his screen, 3 Nov) is not said. */}
        {e.label && !isDesk(e) && <Fact label="Label">{e.label}</Fact>}
        {e.partyRole === 'merchant' && !isDesk(e) && <Fact label="Payee">Merchant</Fact>}
        <Fact label="Network">{networkWord(e.chain)}</Fact>
        {/* The head only: the ending is what the signing sheet asks him to type. */}
        <Fact label="Address"><span className="font-mono">{payoutHead(e.address)}</span></Fact>
        {approvedByBooks && <Fact label="Approved">{e.approvedBy ?? '—'} · {relativeTime(e.approvedAt!)}{e.approvedByOwner === false ? ' · not the owner' : ''}</Fact>}
        {group === 'awaiting' && e.proposedBy && <Fact label="Proposed by">{e.proposedBy}</Fact>}
        {e.testReceivedAt && <Fact label="Test">received · {relativeTime(e.testReceivedAt)} · {e.testReceivedBy ?? '—'}</Fact>}
        {/* KYT is information (the operator, 3 Oct): its own word as today, never a verdict column. */}
        {e.kytVerdict && <Fact label="KYT">{e.kytVerdict}{e.kytExpiresAt ? ` · expires ${until(e.kytExpiresAt)}` : ''}</Fact>}
      </div>
    </DetailSheet>
  );
}
